/* eslint-disable max-lines */
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import FormWrapper from "../../components/ui/FormWrapper";
import Input from "../../components/ui/input/Input";
import useFetch from "../../hooks/useFetch";
import useNavigateState from "../../hooks/useNavigateState";
import {
  IEsimPartnerCreate,
  IEsimPartnerUpdate
} from "../../interfaces/endpoints/IEsimPartner";
import {
  createEsimPartner,
  getEsimPartnerById,
  updateEsimPartner
} from "../../services/EsimPartner.service";
import EsimPartnerSchema from "../../utils/validations/EsimPartner.schema";

interface FormShape {
  companyName: string;
  allowedEmail: string;
  logoUrl: string;
  helpCenterUrl?: string;
  isActive: boolean;
}

const EsimPartnerForm: React.FC<{ editMode?: boolean }> = ({ editMode }) => {
  const { id } = useParams();
  const stateCrumbs = useNavigateState(["Partneret eSIM"]);
  const { state } = useLocation() as { state: { page?: number } | null };

  const methods = useForm<FormShape>({
    // yupResolver's inferred shape doesn't always line up with FormShape (e.g. yup's
    // .transform() can widen optionals to null) — same shim used in AffiliateForm.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(EsimPartnerSchema) as any,
    defaultValues: {
      isActive: true
    }
  });

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    formState: { errors, isSubmitSuccessful, dirtyFields }
  } = methods;
  const navigate = useNavigate();

  const { response } = useFetch(getEsimPartnerById, {
    initRequest: !!editMode,
    params: Number(id)
  });

  useEffect(() => {
    if (editMode && response && response.data) {
      const data = response.data;
      setValue("companyName", data.companyName);
      setValue("allowedEmail", data.allowedEmail);
      setValue("logoUrl", data.logoUrl);
      setValue("helpCenterUrl", data.helpCenterUrl || "");
      setValue("isActive", data.isActive);
    }
  }, [response, id, editMode, setValue]);

  const handleSubmitError = (error: unknown) => {
    const responseData =
      error && typeof error === "object" && "response" in error
        ? (
            error as {
              response?: {
                data?: {
                  message?: string;
                  errors?: Record<string, string[]>;
                };
              };
            }
          ).response?.data
        : undefined;
    const fieldErrors = responseData?.errors;

    if (fieldErrors) {
      Object.entries(fieldErrors).forEach(([field, messages]) => {
        const fieldKey = field.charAt(0).toLowerCase() + field.slice(1);
        setError(fieldKey as keyof FormShape, {
          message: messages?.[0]
        });
      });
    } else {
      toast.error(responseData?.message || "Ndodhi nje gabim gjate ruajtjes");
    }
  };

  const onSubmit: SubmitHandler<FormShape> = async (data) => {
    const helpCenterUrl = data.helpCenterUrl?.trim() || undefined;

    if (editMode) {
      const body: IEsimPartnerUpdate = {
        companyName: data.companyName,
        allowedEmail: data.allowedEmail,
        logoUrl: data.logoUrl,
        helpCenterUrl,
        isActive: data.isActive
      };
      await updateEsimPartner(body, Number(id))
        .then(() => {
          toast.success("Partneri u modifikua me sukses");
          setTimeout(
            () =>
              navigate(
                `/esim-partners?pageindex=${state?.page ? state?.page : 1}`,
                { state: stateCrumbs }
              ),
            500
          );
        })
        .catch(handleSubmitError);
    } else {
      const body: IEsimPartnerCreate = {
        companyName: data.companyName,
        allowedEmail: data.allowedEmail,
        logoUrl: data.logoUrl,
        helpCenterUrl
      };
      await createEsimPartner(body)
        .then(() => {
          toast.success("Partneri u shtua me sukses");
          setTimeout(
            () =>
              navigate("/esim-partners", { state: ["Partneret eSIM"] }),
            500
          );
        })
        .catch(handleSubmitError);
    }
  };

  return (
    <FormProvider {...methods}>
      <Link
        className="flex items-center mb-4 text-sm font-semibold text-primary"
        to="/esim-partners"
        state={["Partneret eSIM"]}
      >
        <ChevronLeftIcon className="w-4 h-4 mr-2" />
        <span>Kthehu tek lista</span>
      </Link>
      <FormWrapper
        showPrompt={!isSubmitSuccessful && !!Object.keys(dirtyFields).length}
        onSubmit={handleSubmit(onSubmit)}
        title={editMode ? "Modifiko partnerin" : "Shto partner te ri"}
      >
        <Input
          {...register("companyName")}
          label="Emri i kompanise"
          error={errors.companyName?.message}
        />
        <Input
          {...register("allowedEmail")}
          label="Email-i qe do te kete qasje"
          type="email"
          error={errors.allowedEmail?.message}
        />
        <Input
          {...register("logoUrl")}
          label="URL e logos (kopjoje pas ngarkimit ne CDN)"
          error={errors.logoUrl?.message}
        />
        <Input
          {...register("helpCenterUrl")}
          label="URL e Help Center (opsionale, paraprakisht: Gjirafa Travel)"
          error={errors.helpCenterUrl?.message}
        />
        {editMode && (
          <div className="col-span-full flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              id="isActive"
              {...register("isActive")}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              Aktiv (kur joaktiv, partneri humb qasjen ne portal)
            </label>
          </div>
        )}
      </FormWrapper>
    </FormProvider>
  );
};

export default EsimPartnerForm;
